<script setup lang="ts">
/**
 * Drum picker — RedisiginProductPage language, pre-redesign interaction.
 *
 * A scrolling reel with the value locked under a centre rule. Orientation
 * comes from the measurement itself: lengths (рост, длина рукава) read
 * vertically, girths (обхваты) read horizontally, as the API marks them.
 * Shared by the profile parameter dialog and the size survey.
 *
 * Scroll-snap does the physics; the component only reports which item ended up
 * under the rule and scrolls the selection back into place when the model
 * changes from outside.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import '@/ui/tokens/fair-drum-picker.css'

interface FairDrumPickerProps {
  modelValue: number | null
  items: number[]
  orientation?: 'vertical' | 'horizontal'
  unit?: string
  ariaLabel?: string
}

const props = withDefaults(defineProps<FairDrumPickerProps>(), {
  orientation: 'vertical',
  unit: '',
  ariaLabel: 'Значение',
})

const emit = defineEmits<{
  'update:modelValue': [value: number | null]
}>()

const reelRef = ref<HTMLElement | null>(null)
const itemRefs = ref<HTMLElement[]>([])

/** Set while we drive the scroll ourselves, so the handler stays quiet. */
const isSyncing = ref(false)
const settleTimer = ref<ReturnType<typeof setTimeout> | null>(null)

const isHorizontal = computed(() => props.orientation === 'horizontal')

const selectedIndex = computed(() => {
  if (props.modelValue === null) return -1
  return props.items.indexOf(props.modelValue)
})

function setItemRef(el: Element | null, index: number) {
  if (el instanceof HTMLElement) {
    itemRefs.value[index] = el
  }
}

function scrollToIndex(index: number, behavior: ScrollBehavior = 'smooth') {
  const reel = reelRef.value
  const item = itemRefs.value[index]
  if (!reel || !item) return

  isSyncing.value = true
  reel.scrollTo({
    top: isHorizontal.value
      ? undefined
      : item.offsetTop - (reel.clientHeight - item.offsetHeight) / 2,
    left: isHorizontal.value
      ? item.offsetLeft - (reel.clientWidth - item.offsetWidth) / 2
      : undefined,
    behavior,
  })

  window.setTimeout(() => {
    isSyncing.value = false
  }, behavior === 'smooth' ? 320 : 0)
}

/** Whichever item's centre sits closest to the reel's centre wins. */
function nearestIndex(): number {
  const reel = reelRef.value
  if (!reel) return -1

  const centre = isHorizontal.value
    ? reel.scrollLeft + reel.clientWidth / 2
    : reel.scrollTop + reel.clientHeight / 2

  let best = -1
  let bestDistance = Number.POSITIVE_INFINITY

  itemRefs.value.forEach((item, index) => {
    if (!item) return
    const itemCentre = isHorizontal.value
      ? item.offsetLeft + item.offsetWidth / 2
      : item.offsetTop + item.offsetHeight / 2
    const distance = Math.abs(itemCentre - centre)
    if (distance < bestDistance) {
      bestDistance = distance
      best = index
    }
  })

  return best
}

function handleScroll() {
  if (isSyncing.value) return

  if (settleTimer.value) {
    clearTimeout(settleTimer.value)
  }

  settleTimer.value = setTimeout(() => {
    const index = nearestIndex()
    const value = props.items[index]
    if (value !== undefined && value !== props.modelValue) {
      emit('update:modelValue', value)
    }
  }, 90)
}

function handleItemClick(index: number) {
  emit('update:modelValue', props.items[index] ?? null)
  scrollToIndex(index)
}

function step(delta: number) {
  const current = selectedIndex.value >= 0 ? selectedIndex.value : 0
  const next = Math.min(props.items.length - 1, Math.max(0, current + delta))
  emit('update:modelValue', props.items[next] ?? null)
  scrollToIndex(next)
}

function handleKeydown(event: KeyboardEvent) {
  const back = isHorizontal.value ? 'ArrowLeft' : 'ArrowUp'
  const forward = isHorizontal.value ? 'ArrowRight' : 'ArrowDown'

  if (event.key === back) {
    event.preventDefault()
    step(-1)
  } else if (event.key === forward) {
    event.preventDefault()
    step(1)
  }
}

/**
 * Re-centre when the value or the reel's contents change. Opening the dialog
 * mounts the reel with zero size, hence the `nextTick`.
 */
watch(
  () => [props.modelValue, props.items] as const,
  () => {
    void nextTick(() => {
      const index = selectedIndex.value
      if (index >= 0) {
        scrollToIndex(index, 'auto')
      }
    })
  },
  { immediate: true, deep: true },
)

onBeforeUnmount(() => {
  if (settleTimer.value) {
    clearTimeout(settleTimer.value)
  }
})

defineExpose({ recenter: () => scrollToIndex(selectedIndex.value, 'auto') })
</script>

<template>
  <div
    class="fair-drum"
    :class="`fair-drum--${orientation}`"
  >
    <div class="fair-drum__readout">
      <span class="fair-drum__readout-value">
        {{ modelValue ?? '—' }}
      </span>
      <span
        v-if="unit && modelValue !== null"
        class="fair-drum__readout-unit"
      >
        {{ unit }}
      </span>
    </div>

    <div class="fair-drum__stage">
      <!-- Centre rule: the value that counts is the one between the lines. -->
      <span
        class="fair-drum__rule"
        aria-hidden="true"
      />

      <div
        ref="reelRef"
        class="fair-drum__reel"
        role="listbox"
        :aria-label="ariaLabel"
        :aria-orientation="orientation"
        tabindex="0"
        @scroll="handleScroll"
        @keydown="handleKeydown"
      >
        <button
          v-for="(item, index) in items"
          :ref="(el) => setItemRef(el as Element | null, index)"
          :key="item"
          type="button"
          role="option"
          class="fair-drum__item"
          :class="{
            'fair-drum__item--selected': index === selectedIndex,
          }"
          :aria-selected="index === selectedIndex"
          tabindex="-1"
          @click="handleItemClick(index)"
        >
          {{ item }}
        </button>
      </div>
    </div>
  </div>
</template>
